import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ApiError, type AdminOutput, type NodeInfo, type NodeRef } from '../../api'
import { useCenterNow } from './clock'
import { NODES_KEY, fetchNodes } from '../Nodes'
import {
  RELAY,
  relayKeys,
  type RelayEpoch,
  type RelayJournalEvent,
  type RelayList,
  type RelayRoute,
  type RelayRouteDetail,
  type RelayStatsSeries,
  type RelayTraceSample,
  type StatsRange,
  ROUTES_PATH,
} from './api'
import { ago, clock, dur, ms, pct, seq } from './format'
import { useRelayLive } from './live'
import { RelayDataBanner } from './banner'
import { errorText, useRelayGet } from './queries'
import { CHANGED_BY, EPOCH_SPEC, HOP_SPEC, MODE_SPEC, OUTPUT_SPEC, ROUTE_SPEC, reasonText } from './states'
import { Copy, Empty, EpochTag, ErrorBox, Fact, NetPanel, PlatSlot, SkeletonRows, StateBadge, StateText, platformOf } from './ui'
import { NodeName, PathLine, roleAt } from './path'
import { SeriesChart, toChart } from './SeriesChart'
import { TraceSample } from './TraceSample'
import { EventList } from './events'

/*
 * The route card, read-only (design #r0 (b), handoff/routes.md):
 * /admin/network/routes/:id. Path and hops, epoch and its history, the
 * series (latency, frames) and sampled frame traces, counters, the route's
 * relay events and the stream's outputs. No switch or pin before R1/R2.
 * Live: SSE route / journal events update it; without the stream it polls.
 */

const RANGES: StatsRange[] = ['15m', '1h', '6h', '24h']

const H2 = ({ children }: { children: ReactNode }) => <h2 className="text-ui-title font-medium">{children}</h2>

export function RouteCardPage() {
  const { id = '' } = useParams()
  const live = useRelayLive()
  const detail = useRelayGet<RelayRouteDetail>(relayKeys.route(id), `${RELAY}/routes/${encodeURIComponent(id)}`, live, { poll: true })
  const nodesQ = useQuery({ queryKey: NODES_KEY, queryFn: fetchNodes, refetchInterval: 10_000, enabled: live.available === true })
  const nodes = new Map<string, NodeInfo>((nodesQ.data ?? []).map((n) => [n.id, n]))
  const head = (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Link to={ROUTES_PATH} className="text-sm text-muted hover:text-text" data-testid="route-crumb">
        Routes
      </Link>
      <span className="text-faint" aria-hidden>
        /
      </span>
      <h1 className="text-xl font-medium">
        Route <span className="font-mono">{id}</span>
      </h1>
    </div>
  )

  let body: ReactNode
  if (live.available === false) {
    body = (
      <Empty title="The routing network is not available" testId="route-unavailable">
        This center has no relay API. Routes appear here once the center runs the routing network (R0).
      </Empty>
    )
  } else if (detail.isPending) {
    body = (
      <NetPanel>
        <SkeletonRows rows={8} cols={6} />
      </NetPanel>
    )
  } else if (detail.error) {
    const e = detail.error
    body =
      e instanceof ApiError && e.status === 404 ? (
        <Empty
          title={`No route ${id}`}
          testId="route-not-found"
          action={
            <Link to="/admin/streams" className="inline-flex min-h-9 items-center rounded-md border border-line-2 px-3 text-sm font-medium hover:border-slot">
              Back to streams
            </Link>
          }
        >
          The route may have ended and left the retention window, or the link is wrong.
        </Empty>
      ) : (
        <ErrorBox testId="route-error" text={errorText(`${RELAY}/routes/${id}`, e)} busy={detail.isFetching} onRetry={() => void detail.refetch()} />
      )
  } else {
    // live fields of the stream over the card's answer when newer
    const lr = live.routes.get(id)
    const d = detail.data.data
    const r: RelayRouteDetail = lr && (!d.as_of || !lr.as_of || Date.parse(lr.as_of) >= Date.parse(d.as_of)) ? { ...d, ...lr, as_of: lr.as_of ?? d.as_of, recent_events: d.recent_events } : d
    body = <RouteCard r={r} nodes={nodes} liveJournal={live.journal} />
  }

  return (
    <div data-testid="route-card-page">
      {head}
      <RelayDataBanner live={live} />
      {body}
    </div>
  )
}

function RouteCard({ r, nodes, liveJournal }: { r: RelayRouteDetail; nodes: ReadonlyMap<string, NodeInfo>; liveJournal: readonly RelayJournalEvent[] }) {
  const live = useRelayLive()
  const now = useCenterNow(1000)
  const spec = ROUTE_SPEC[r.state]
  const why = reasonText(r.state_reason)
  const rolesOf = (id: string) => nodes.get(id)?.roles
  const offline = (id: string) => {
    const n = nodes.get(id)
    return !!n && n.status !== 'online'
  }
  const nameOf = (id?: string) => (id ? (nodes.get(id)?.name ?? r.path.find((p) => p.id === id)?.name ?? id) : '—')
  const outputsQ = useRelayGet<AdminOutput[]>(relayKeys.outputs(r.stream), `/api/admin/outputs?stream=${encodeURIComponent(r.stream)}`, live, { poll: true })
  const outputs = outputsQ.data?.data ?? []
  const outLive = outputs.filter((o) => o.state === 'live').length
  const egress = r.path[r.path.length - 1] ?? r.path[0]!
  const ended = r.state === 'ended'

  return (
    <div className="flex flex-col gap-4" data-testid="route-card" data-route={r.route_id} data-state={r.state}>
      <NetPanel>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
            <div className="flex min-w-0 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[17px] font-medium break-all" data-testid="route-stream">
                  {r.stream}
                </span>
                <Copy value={r.route_id} label="route id" />
              </span>
              {r.user?.email && <span className="font-mono text-xs text-muted">{r.user.email}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StateBadge spec={spec} testId="route-card-state" />
              <StateText spec={MODE_SPEC[r.mode]} />
              <EpochTag e={r.epoch} />
            </div>
            <span className="text-xs text-muted sm:ml-auto" data-testid="route-readonly">
              Read-only: changing a route comes with planned switches.
            </span>
          </div>
          {(why || ended) && (
            <p className={`text-sm ${spec.color === 'fail' ? 'text-fail' : spec.color === 'warn' ? 'text-warn' : 'text-text-soft'}`} data-testid="route-card-reason">
              <span aria-hidden className="font-mono">
                {spec.glyph}
              </span>{' '}
              {ended ? `Ended${r.end_reason ? `: ${r.end_reason.replace(/_/g, ' ')}` : ''}` : why} · since <span className="tabular">{clock(r.ended_at ?? r.state_since)}</span> ({ago(r.ended_at ?? r.state_since, now)})
              {r.source_grace_until && Date.parse(r.source_grace_until) > now && (
                <span className="text-text-soft">
                  {' '}
                  · outputs hold the platform sessions for <span className="tabular">{Math.ceil((Date.parse(r.source_grace_until) - now) / 1000)} s</span> more
                </span>
              )}
            </p>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3 lg:grid-cols-6">
            <Fact k="End-to-end p50 / p99" testId="route-e2e">
              <span className="tabular">{r.state === 'no_path' || r.e2e_p50_ms == null ? '—' : `${ms(r.e2e_p50_ms)} / ${ms(r.e2e_p99_ms)}`}</span>
            </Fact>
            <Fact k="Jitter buffer">
              <span className="tabular">{r.jitter_ms ? ms(r.jitter_ms) : 'off'}</span>
            </Fact>
            <Fact k="Outputs">
              <span className="inline-flex flex-wrap items-center gap-1.5">
                <span className={`num ${outLive < (r.outputs.total || outputs.length) ? 'text-warn' : ''}`}>
                  {outputs.length ? outLive : (r.outputs.by_state.live ?? 0)}/{r.outputs.total || outputs.length} live
                </span>
                {outputs.map((o) => (
                  <PlatSlot key={o.output_id} p={platformOf(o.profile)} size={18} dim={o.state !== 'live'} />
                ))}
              </span>
            </Fact>
            <Fact k="Changed by">
              <span className={CHANGED_BY[r.changed_by].cls}>{CHANGED_BY[r.changed_by].word}</span>
              <span className="tabular ml-1.5 text-xs text-muted">{clock(r.changed_at)}</span>
            </Fact>
            <Fact k="Source">
              <span className={r.source && !r.source.live ? 'text-warn' : 'text-text-soft'}>{r.source ? (r.source.live ? <>OBS live · {dur(r.source.since, now)}</> : 'OBS not sending') : '—'}</span>
            </Fact>
            <Fact k="Created">
              <span className="tabular text-text-soft">
                {clock(r.created_at)} · {ago(r.created_at, now)}
              </span>
            </Fact>
          </div>
        </div>
      </NetPanel>

      {/* path ribbon: OBS → ingress → hop → hub → platforms */}
      <NetPanel testId="route-path">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
          <span className="grid h-[22px] place-items-center rounded-sm border border-slot px-1 font-mono text-[9px]" title="The encoder">
            OBS
          </span>
          <span className="text-faint" aria-hidden>
            —
          </span>
          <PathLine path={r.path} hops={r.hops} rolesOf={rolesOf} offline={offline} size="md" />
          <span className="text-faint" aria-hidden>
            —
          </span>
          <span className="inline-flex flex-wrap items-center gap-1.5">
            {outputs.map((o) => (
              <PlatSlot key={o.output_id} p={platformOf(o.profile)} dim={o.state !== 'live'} />
            ))}
            <span className="font-mono text-xs text-text-soft">
              {outLive}/{outputs.length} live
            </span>
          </span>
        </div>
      </NetPanel>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <Hops r={r} />
          <Epochs id={r.route_id} />
          <Series r={r} journal={r.recent_events} nameOf={nameOf} />
          <Traces id={r.route_id} outputs={outputs} nameOf={nameOf} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <OutputsPanel q={outputsQ} outputs={outputs} egress={egress} nodes={nodes} />
          <Events r={r} liveJournal={liveJournal} nameOf={nameOf} />
        </div>
      </div>
    </div>
  )
}

/* ── hops ─────────────────────────────────────────────────────────────────── */

function Hops({ r }: { r: RelayRoute }) {
  const now = useCenterNow(5000)
  return (
    <NetPanel title={<H2>Hops</H2>} pad={false} testId="route-hops">
      {r.hops.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted">No hops: the route has no path.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="sx-table compact [&_td]:whitespace-normal [&_th]:whitespace-normal [&_td]:py-1.5 [&_td]:align-top min-w-[640px]">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">From → to</th>
                <th scope="col">State</th>
                <th scope="col">Dialer</th>
                <th scope="col" className="text-right">Latency p50 / p99</th>
                <th scope="col" className="text-right">RTT</th>
                <th scope="col" className="text-right">Retrans</th>
                <th scope="col">Since</th>
              </tr>
            </thead>
            <tbody>
              {r.hops.map((h) => {
                const down = h.state === 'down'
                return (
                  <tr key={h.index} data-testid={`hop-${h.index}`} data-state={h.state}>
                    <td className="tabular text-muted">{h.index + 1}</td>
                    <td className="font-mono text-xs whitespace-nowrap">
                      {h.from.name} <span className="text-faint" aria-hidden>→</span>
                      <span className="sr-only"> to </span> {h.to.name}
                    </td>
                    <td>
                      <StateText spec={HOP_SPEC[h.state]} />
                    </td>
                    <td className="text-xs text-text-soft">{h.dialer === 'down' ? `${h.to.name} dials` : h.dialer === 'up' ? `${h.from.name} dials` : '—'}</td>
                    <td className="tabular text-right whitespace-nowrap">{down ? '—' : `${ms(h.lat_p50_ms)} / ${ms(h.lat_p99_ms)}`}</td>
                    <td className="tabular text-right">{down ? '—' : ms(h.rtt_ms)}</td>
                    <td className={`tabular text-right ${(h.retrans_pct ?? 0) >= 1 ? 'text-warn' : ''}`}>{down ? '—' : pct(h.retrans_pct)}</td>
                    <td className="tabular text-xs whitespace-nowrap text-muted">{ago(h.since, now)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </NetPanel>
  )
}

/* ── epochs ───────────────────────────────────────────────────────────────── */

function PathPlain({ path }: { path: NodeRef[] }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 font-mono text-xs text-text-soft">
      {path.map((p, i) => (
        <span key={`${p.id}-${i}`} className="inline-flex items-center gap-1.5">
          {i > 0 && <span className="text-faint">→</span>}
          {p.name}
        </span>
      ))}
    </span>
  )
}

function Epochs({ id }: { id: string }) {
  const live = useRelayLive()
  const q = useRelayGet<{ as_of: string; items: RelayEpoch[] }>(relayKeys.epochs(id), `${RELAY}/routes/${encodeURIComponent(id)}/epochs`, live)
  return (
    <NetPanel title={<H2>Epoch history</H2>} aside={<span className="text-xs text-muted">One epoch per path; planned switches add more</span>} pad={false} testId="route-epochs">
      {q.isPending ? (
        <div className="p-4">
          <SkeletonRows rows={2} cols={6} />
        </div>
      ) : q.error ? (
        <div className="p-4">
          <ErrorBox text={errorText(`${RELAY}/routes/${id}/epochs`, q.error)} busy={q.isFetching} onRetry={() => void q.refetch()} />
        </div>
      ) : q.data.data.items.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted">No epochs recorded.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="sx-table compact [&_td]:whitespace-normal [&_th]:whitespace-normal [&_td]:py-1.5 [&_td]:align-top min-w-[640px]">
            <thead>
              <tr>
                <th scope="col">Epoch</th>
                <th scope="col">State</th>
                <th scope="col">Path</th>
                <th scope="col">Origin</th>
                <th scope="col" className="text-right">Cut-over seq</th>
                <th scope="col">Activated</th>
                <th scope="col">Retired</th>
              </tr>
            </thead>
            <tbody>
              {q.data.data.items.map((e) => (
                <tr key={`${e.epoch}-${e.kind}`} aria-selected={e.state === 'active' || undefined}>
                  <td>
                    <EpochTag e={e.epoch} />
                  </td>
                  <td>
                    <StateText spec={EPOCH_SPEC[e.state]} />
                  </td>
                  <td>
                    <PathPlain path={e.path} />
                  </td>
                  <td className="text-xs">
                    <span className={CHANGED_BY[e.origin].cls}>{CHANGED_BY[e.origin].word}</span>
                    {e.actor && <span className="text-muted"> · {e.actor}</span>}
                  </td>
                  <td className="tabular text-right text-xs">{seq(e.cutover_seq)}</td>
                  <td className="tabular text-xs whitespace-nowrap">{clock(e.activated_at)}</td>
                  <td className="tabular text-xs whitespace-nowrap text-muted">{e.retired_at ? clock(e.retired_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </NetPanel>
  )
}

/* ── series and counters ──────────────────────────────────────────────────── */

const COUNTERS: [keyof RelayRoute['counters'], string, string][] = [
  ['gaps', 'gaps', 'Times frames were missing at the egress'],
  ['gap_frames', 'gap frames', 'Frames lost in gaps'],
  ['dups', 'dups', 'Duplicate frames dropped'],
  ['reorder', 'reorder', 'Frames that arrived out of order'],
  ['epoch_drops', 'epoch drops', 'Frames of an old epoch dropped after a cut-over'],
  ['resumes', 'resumes', 'Hops that reconnected and caught up from the ring'],
]

function Series({ r, journal, nameOf }: { r: RelayRoute; journal: RelayJournalEvent[]; nameOf: (id?: string) => string }) {
  const live = useRelayLive()
  const [range, setRange] = useState<StatsRange>('15m')
  const path = `${RELAY}/routes/${encodeURIComponent(r.route_id)}/stats?range=${range}`
  const q = useRelayGet<RelayStatsSeries>(relayKeys.stats(r.route_id, range), path, live, { poll: true })
  const c = r.counters
  const chart = q.data && toChart(q.data.data, journal, (x) => nameOf(x))
  return (
    <NetPanel
      title={<H2>Latency and frames</H2>}
      testId="route-series"
      aside={
        <span role="group" aria-label="Time range" className="flex gap-1">
          {RANGES.map((x) => (
            <button
              key={x}
              type="button"
              aria-pressed={range === x}
              onClick={() => setRange(x)}
              className={`tabular inline-flex min-h-8 items-center rounded-sm border px-2.5 text-xs max-md:min-h-[44px] ${range === x ? 'border-live/50 bg-live/[.12] text-live' : 'border-line-2 text-muted hover:text-text'}`}
            >
              {x}
            </button>
          ))}
        </span>
      }
    >
      <div className="flex flex-col gap-4">
        {q.isPending ? (
          <SkeletonRows rows={4} cols={8} />
        ) : q.error ? (
          <ErrorBox text={errorText(path, q.error)} busy={q.isFetching} onRetry={() => void q.refetch()} />
        ) : !chart || chart.t.length === 0 ? (
          <Empty title={`No data in the last ${range}`} testId="series-empty">
            The nodes send a report every 5 s while the route carries a stream. Older data may have left the retention window.
          </Empty>
        ) : (
          <>
            <SeriesChart c={chart} label={`Latency per hop, end-to-end and frames for ${r.route_id}, last ${range}, one point per ${q.data.data.step}`} />
            <details className="text-xs text-muted">
              <summary className="cursor-pointer text-text-soft">Show as table</summary>
              <div className="mt-2 max-h-56 overflow-auto">
                <table className="sx-table compact [&_td]:whitespace-normal [&_th]:whitespace-normal [&_td]:py-1.5 [&_td]:align-top">
                  <thead>
                    <tr>
                      <th scope="col">Time</th>
                      {chart.hops.map((h) => (
                        <th scope="col" key={h.name} className="text-right">
                          {h.name}
                        </th>
                      ))}
                      <th scope="col" className="text-right">End-to-end</th>
                      <th scope="col" className="text-right">Frames</th>
                      <th scope="col" className="text-right">Lost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chart.t.map(
                      (t, i) =>
                        i % Math.max(1, Math.ceil(chart.t.length / 20)) === 0 && (
                          <tr key={t}>
                            <td className="tabular">{clock(t)}</td>
                            {chart.hops.map((h) => (
                              <td key={h.name} className="tabular text-right">
                                {ms(h.values[i])}
                              </td>
                            ))}
                            <td className="tabular text-right">{ms(chart.e2e[i])}</td>
                            <td className="tabular text-right">{chart.frames[i] ?? '—'}</td>
                            <td className="tabular text-right">{chart.gaps[i] ?? '—'}</td>
                          </tr>
                        ),
                    )}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        )}
        <div className="grid grid-cols-3 gap-x-4 gap-y-3 md:grid-cols-6" data-testid="route-counters">
          {COUNTERS.map(([k, label, hint]) => (
            <div key={k} className="flex flex-col" title={hint}>
              <span className="mono-label uppercase">{label}</span>
              <span className={`tabular text-lg font-medium ${c[k] > 0 && k !== 'dups' && k !== 'resumes' ? 'text-warn' : ''}`} data-testid={`counter-${k}`}>
                {c[k]}
              </span>
            </div>
          ))}
        </div>
        <p className="text-[13px] text-text-soft" data-testid="route-last-resume">
          {r.last_resume?.at ? (
            <>
              Last resume <span className="tabular">{clock(r.last_resume.at)}</span>:{' '}
              {r.last_resume.result === 'ring' ? 'caught up from the ingress ring' : r.last_resume.result === 'gap' ? 'the ring did not cover the outage, gap' : 'fresh start'} · outage{' '}
              <span className="tabular">{ms(r.last_resume.outage_ms)}</span> · <span className="tabular">{r.last_resume.gap_frames ?? 0}</span> frames lost
            </>
          ) : (
            <span className="text-muted">No resumes on this route.</span>
          )}
          <span className="block text-[11px] text-muted">Counters since the route started, as this center process saw the egress reports.</span>
        </p>
      </div>
    </NetPanel>
  )
}

/* ── traces ───────────────────────────────────────────────────────────────── */

function Traces({ id, outputs, nameOf }: { id: string; outputs: AdminOutput[]; nameOf: (id?: string) => string }) {
  const live = useRelayLive()
  const path = `${RELAY}/routes/${encodeURIComponent(id)}/traces?limit=20`
  const q = useRelayGet<RelayList<RelayTraceSample>>(relayKeys.traces(id), path, live)
  const [pick, setPick] = useState(0)
  const items = q.data?.data.items ?? []
  useEffect(() => {
    if (pick >= items.length) setPick(0)
  }, [items.length, pick])
  return (
    <NetPanel title={<H2>Frame traces</H2>} aside={<span className="text-xs text-muted">Sampled frames, the last hour</span>} testId="route-traces">
      {q.isPending ? (
        <SkeletonRows rows={3} cols={5} />
      ) : q.error ? (
        <ErrorBox text={errorText(path, q.error)} busy={q.isFetching} onRetry={() => void q.refetch()} />
      ) : items.length === 0 ? (
        <Empty title="No sampled frames in the last hour" testId="traces-empty">
          A node samples one frame every few seconds and every frame around a gap or a resume. Traces show up here once the route carries a stream.
        </Empty>
      ) : (
        <div className="flex flex-col gap-3">
          <TraceSample tr={items[pick] ?? items[0]!} outputs={outputs} nameOf={(x) => nameOf(x)} />
          {items.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Sampled frames">
              <span className="mono-label mr-1">OTHER FRAMES</span>
              {items.slice(0, 10).map((t, i) => (
                <button
                  key={`${t.seq}-${t.t}`}
                  type="button"
                  aria-pressed={i === pick}
                  onClick={() => setPick(i)}
                  className={`tabular inline-flex min-h-8 items-center gap-1 rounded-sm border px-2 font-mono text-[11px] max-md:min-h-[44px] ${i === pick ? 'border-accent text-text' : t.reason !== 'sampled' ? 'border-warn/50 text-warn' : 'border-line-2 text-muted hover:text-text'}`}
                >
                  {clock(t.t)}
                  {t.reason !== 'sampled' && <span>· {t.reason}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </NetPanel>
  )
}

/* ── outputs ──────────────────────────────────────────────────────────────── */

function OutputsPanel({ q, outputs, egress, nodes }: { q: ReturnType<typeof useRelayGet<AdminOutput[]>>; outputs: AdminOutput[]; egress: NodeRef; nodes: ReadonlyMap<string, NodeInfo> }) {
  return (
    <NetPanel
      title={<H2>Outputs</H2>}
      aside={<NodeName node={egress} role={roleAt(1, 2, nodes.get(egress.id)?.roles)} />}
      pad={false}
      testId="route-outputs"
    >
      {q.isPending ? (
        <div className="p-4">
          <SkeletonRows rows={3} cols={3} />
        </div>
      ) : q.error ? (
        <div className="p-4">
          <ErrorBox text={errorText('/api/admin/outputs', q.error)} busy={q.isFetching} onRetry={() => void q.refetch()} />
        </div>
      ) : outputs.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted">No outputs for this stream.</p>
      ) : (
        <ul className="divide-y divide-line">
          {outputs.map((o) => (
            <li key={o.output_id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`route-output-${o.output_id}`}>
              <PlatSlot p={platformOf(o.profile)} size={24} dim={o.state !== 'live'} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{o.name}</span>
                <span className="num block text-[11px] text-muted">
                  {o.state === 'live' ? `${(o.bitrate_kbps / 1000).toFixed(1)} Mb/s` : '—'}
                  {o.node && <span> · {o.node.name}</span>}
                </span>
              </span>
              <StateText spec={OUTPUT_SPEC[o.state] ?? OUTPUT_SPEC.unknown} />
            </li>
          ))}
        </ul>
      )}
    </NetPanel>
  )
}

/* ── events ───────────────────────────────────────────────────────────────── */

function Events({ r, liveJournal, nameOf }: { r: RelayRouteDetail; liveJournal: readonly RelayJournalEvent[]; nameOf: (id?: string) => string }) {
  const live = useRelayLive()
  const path = `${RELAY}/journal?route_id=${encodeURIComponent(r.route_id)}&limit=50`
  const q = useRelayGet<RelayList<RelayJournalEvent>>(relayKeys.journal(r.route_id), path, live)
  // stored ids differ from the stream's ids: the same event matches by time, kind and node
  const key = (e: RelayJournalEvent) => `${e.t}|${e.kind}|${e.node_id}`
  const seen = new Set<string>()
  const all = [...liveJournal.filter((e) => e.route_id === r.route_id), ...(q.data?.data.items ?? r.recent_events)]
    .filter((e) => (seen.has(key(e)) ? false : (seen.add(key(e)), true)))
    .sort((a, b) => Date.parse(b.t) - Date.parse(a.t))
  return (
    <NetPanel title={<H2>Route events</H2>} aside={<span className="text-xs text-muted">{live.sse === 'open' ? 'live' : 'every 5 s'}</span>} pad={false} testId="route-events-panel">
      {q.error && !all.length ? (
        <div className="p-4">
          <ErrorBox text={errorText(path, q.error)} busy={q.isFetching} onRetry={() => void q.refetch()} />
        </div>
      ) : (
        <EventList events={all.slice(0, 50)} nameOf={nameOf} />
      )}
    </NetPanel>
  )
}
